import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { BankDossier, BankFile } from '../../../../api/workOrders/bankDossier';
import { WorkOrderData } from '../../../../api/workOrders/types';
import { Constants } from '../../../../constants/bankDossier';
import {
  IconDownloadMS,
  IconPictureAsPdfMS,
} from '../../../../constants/icons';
import {
  dossierDateTime,
  fileSizeLabel,
  textOrEmpty,
} from '../../../../helpers/bankDossier';
import { openExternal } from '../../../../helpers/openExternal';
import useSnackbar from '../../../../hooks/useSnackbar';
import { IOSSwitch } from '../../../UI/Button';
import { StatusPill } from '../../../UI/StatusPill';
import { DossierColumn, DossierTable } from '../../DossierTable';
import { BodyCell, CellSecondary, MutedCell } from '../../DossierTable/styles';
import {
  FilesToolbar,
  FilesToolbarLeft,
  FileTypeCell,
  FileTypeIcon,
} from '../styles';

interface SectionProps {
  dossier: BankDossier;
  workOrder: WorkOrderData;
}

const { columns } = Constants.files;

const fileColumns: DossierColumn[] = [
  { key: 'select', label: '', width: '44px' },
  { key: 'type', label: columns.type },
  { key: 'control', label: columns.control },
  { key: 'os', label: columns.os },
  { key: 'created_at', label: columns.createdAt },
  { key: 'size', label: columns.size, align: 'right' },
  { key: 'actions', label: '', width: '56px' },
];

export function FilesSection({
  dossier,
  workOrder,
}: SectionProps): JSX.Element {
  const [showFolder, setShowFolder] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const { handleSnackbar } = useSnackbar();

  const files = useMemo(() => {
    const visible = showFolder
      ? dossier.files
      : dossier.files.filter((file) => file.work_order_id === workOrder.id);
    // Latest report first, then newest first.
    return [...visible].sort((a, b) => {
      if (a.is_latest_report !== b.is_latest_report) {
        return a.is_latest_report ? -1 : 1;
      }
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
  }, [dossier.files, showFolder, workOrder.id]);

  const visibleIds = files.map((file) => file.id);
  const selectedVisible = selected.filter((id) => visibleIds.includes(id));
  const allSelected =
    files.length > 0 && selectedVisible.length === files.length;
  const someSelected = selectedVisible.length > 0 && !allSelected;

  const toggleFile = (id: string): void => {
    setSelected((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };

  const toggleAll = (): void => {
    setSelected(allSelected ? [] : visibleIds);
  };

  const download = (items: BankFile[]): void => {
    const withUrl = items.filter((item) => item.url);
    withUrl.forEach((item) => openExternal(item.url as string));
    if (withUrl.length < items.length) {
      handleSnackbar(Constants.demoUnavailable, false);
    }
  };

  const renderRow = (row: BankFile): JSX.Element => {
    const isCurrent = row.work_order_id === workOrder.id;
    return (
      <>
        <BodyCell padding="checkbox">
          <Checkbox
            size="small"
            checked={selected.includes(row.id)}
            onChange={() => toggleFile(row.id)}
            inputProps={{ 'aria-label': `${columns.type} ${row.file_type}` }}
          />
        </BodyCell>
        <BodyCell>
          <FileTypeCell>
            <FileTypeIcon>{IconPictureAsPdfMS}</FileTypeIcon>
            <div>
              <strong>{row.file_type}</strong>
              {row.is_latest_report && (
                <>
                  {' '}
                  <StatusPill tone="info">{Constants.files.latest}</StatusPill>
                </>
              )}
              <CellSecondary>{row.file_name}</CellSecondary>
            </div>
          </FileTypeCell>
        </BodyCell>
        <MutedCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {textOrEmpty(row.bank_control_number)}
        </MutedCell>
        <BodyCell sx={isCurrent ? undefined : { color: 'text.secondary' }}>
          {row.work_order_reference > 0
            ? row.work_order_reference
            : Constants.related.notMigrated}
        </BodyCell>
        <BodyCell sx={{ whiteSpace: 'nowrap' }}>
          {dossierDateTime(row.created_at)}
        </BodyCell>
        <MutedCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {fileSizeLabel(row.size_bytes)}
        </MutedCell>
        <BodyCell align="right">
          <Tooltip title={Constants.files.download}>
            <IconButton
              size="small"
              aria-label={`${Constants.files.download} ${row.file_name}`}
              onClick={() => download([row])}
            >
              {IconDownloadMS}
            </IconButton>
          </Tooltip>
        </BodyCell>
      </>
    );
  };

  return (
    <>
      <FilesToolbar>
        <FilesToolbarLeft>
          <FormControlLabel
            control={
              <IOSSwitch
                checked={showFolder}
                onChange={(event) => setShowFolder(event.target.checked)}
              />
            }
            label={Constants.files.showAllFolder}
            sx={{ gap: '10px', marginLeft: 0 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                disabled={files.length === 0}
                onChange={toggleAll}
              />
            }
            label={Constants.files.selectAll}
          />
        </FilesToolbarLeft>
        <Button
          variant="outlined"
          size="small"
          disabled={selectedVisible.length === 0}
          onClick={() =>
            download(files.filter((file) => selectedVisible.includes(file.id)))
          }
        >
          {IconDownloadMS}
          {Constants.files.downloadSelected(selectedVisible.length)}
        </Button>
      </FilesToolbar>

      <DossierTable<BankFile>
        columns={fileColumns}
        rows={files}
        getRowId={(row) => row.id}
        renderRow={renderRow}
        rowClassName={(row) =>
          row.is_latest_report ? 'is-current' : undefined
        }
        emptyMessage={Constants.files.empty}
        ariaLabel={Constants.sections.files}
      />
    </>
  );
}
